import ReactModal from 'react-modal';

import { cn } from 'lib/helpers/utility';
import { DirtyTracker } from 'lib/context/DirtyTrackerContext';
import { ShellPageContext } from 'lib/context/ShellContext/ShellContext';

import Footer from './components/Footer';
import Header from './components/Header';
import useModalSubmit from './lib/useModalSubmit';
import useContentLabel from './lib/useContentLabel';
import SubModal from './components/SubModal/SubModal';
import ModalSidebar from './ModalSidebar/ModalSidebar';
import { useSubModalStack } from './lib/useSubModalStack';
import { useProtectedClose } from './lib/useProtectedClose';
import { bringPortalToFront } from './lib/bringPortalToFront';
import SubModalContext from './components/SubModal/lib/SubModalContext';
import { MODAL_BODY_SPACE, MODAL_HEIGHT, MODAL_HEIGHT_STYLE, MODAL_WIDTH } from './lib/index';

import './lib/Modal.scss';

const BaseModal = ({
  isOpen = false,
  handleClose,
  title,
  customTitle,
  children,
  width = 'small',
  isCentered = false,
  isHeightFit = true,
  isHeightTall = false,
  isHeightFixed = false,
  fixedHeight,
  subModal: controlledSubModal,
  slideFromRight = true,
  slideFromBottom = false,
  isLarge = false,
  disableHeader = false,
  headButton,
  headerClassName,
  headerCloseIcon = true,
  headerCloseIconClassName,
  customHeader,
  prevButton,
  footer,
  isFooter = true,
  footerActions,
  onEnter,
  submitOnEnter = true,
  className,
  portalClassName,
  shouldCloseOnOverlayClick = true,
  overlayClassName,
  bodyClassName,
  footerClassName,
  customStyling,
  modalCenterAnimation = false,
  isSidebar = false,
  sidebarItems = [],
  onScroll,
  listClassName,
  contentClassName,
  activeSidebarKey,
  onActiveChange,
  isHidden = false,
  isDirty,
  onAfterOpen,
  ...rest
}) => {
  const contentLabel = useContentLabel(title, customTitle);

  const { activeSubModal, activeStack, subModalVisible, openSubModal, closeSubModal } =
    useSubModalStack(controlledSubModal);

  const { setContentRef, bodyRef, submitFooterActions, onSubmitClickCapture } = useModalSubmit({
    isOpen,
    isFooter,
    footerActions,
    onEnter,
    submitOnEnter,
    isSubModalOpen: subModalVisible
  });

  const handleSubModalClose = () => {
    // `false` means the handler is taking over the close (e.g. to confirm first),
    // so don't pull the sub-modal out from under it.
    const vetoed = activeSubModal?.actions?.onClose?.() === false;
    if (controlledSubModal === undefined && !vetoed) closeSubModal();
  };

  const protectedClose = useProtectedClose({ isOpen, isDirty, handleClose });

  const hiddenStyle = isHidden ? { opacity: 0, pointerEvents: 'none' } : {};
  const heightStyle = MODAL_HEIGHT_STYLE({ isCentered, isHeightFixed, fixedHeight });

  return (
    <ShellPageContext.Provider value={false}>
      <SubModalContext.Provider value={{ openSubModal, closeSubModal }}>
        <ReactModal
          isOpen={isOpen}
          onAfterOpen={bringPortalToFront(onAfterOpen)}
          onRequestClose={subModalVisible ? handleSubModalClose : protectedClose}
          contentLabel={contentLabel}
          closeTimeoutMS={300}
          className={cn(
            'Modal',
            isCentered && 'Modal--centered',
            MODAL_WIDTH(width, isCentered),
            isCentered && MODAL_HEIGHT({ isHeightFit, isHeightTall, isHeightFixed }),
            !slideFromRight && 'm-6 max-h-[calc(100vh-48px)] sm:m-4 sm:max-h-[calc(100vh-32px)]',
            isLarge && MODAL_WIDTH('full'),
            className
          )}
          overlayClassName={cn(
            'ModalOverlay',
            !isCentered && slideFromRight && 'ModalRightOverlay',
            !isCentered && slideFromBottom && 'ModalBottomOverlay',
            modalCenterAnimation && 'ModalCenterAnimation',
            overlayClassName
          )}
          portalClassName={cn(portalClassName)}
          ariaHideApp={false}
          contentRef={setContentRef}
          shouldCloseOnOverlayClick={shouldCloseOnOverlayClick && !subModalVisible}
          style={{
            content: { ...heightStyle, ...customStyling, ...hiddenStyle },
            overlay: hiddenStyle
          }}
          {...rest}>
          {!disableHeader && (
            <Header
              title={title}
              customTitle={customTitle}
              headButton={headButton}
              headerClassName={headerClassName}
              headerCloseIcon={headerCloseIcon}
              customHeader={customHeader}
              prevButton={prevButton}
              handleClose={protectedClose}
              headerCloseIconClassName={headerCloseIconClassName}
            />
          )}
          <div
            ref={bodyRef}
            onScroll={onScroll}
            className={cn(
              'Modal__body',
              MODAL_BODY_SPACE(width, isCentered, isSidebar),
              bodyClassName
            )}>
            {isSidebar ? (
              <ModalSidebar
                items={sidebarItems}
                className={bodyClassName}
                listClassName={listClassName}
                contentClassName={contentClassName}
                activeKey={activeSidebarKey}
                onActiveChange={onActiveChange}
              />
            ) : (
              children
            )}
          </div>
          <Footer
            footer={footer}
            footerActions={submitFooterActions}
            handleClose={protectedClose}
            className={footerClassName}
            isFooter={isFooter}
            onSubmitClickCapture={onSubmitClickCapture}
          />
          <SubModal
            // Every stack entry stays mounted at a stable position (only the top is
            // visible) — unmounting a lower sheet would also unmount the component
            // that opened the sheet above it, popping it right back off.
            subModalContent={
              activeStack.length > 0
                ? activeStack.map((entry, index) => (
                    <div
                      key={entry.slotKey}
                      className={index === activeStack.length - 1 ? 'contents' : 'hidden'}>
                      {entry.content}
                    </div>
                  ))
                : activeSubModal?.content
            }
            subModalVisible={subModalVisible}
            subModalTitle={activeSubModal?.title}
            subModalClassName={activeSubModal?.className}
            subModalPanelClassName={activeSubModal?.panelClassName}
            subModalActions={activeSubModal?.actions}
            subModalFooter={activeSubModal?.footer}
            subModalHeaderActions={activeSubModal?.headerActions}
            onClose={handleSubModalClose}
          />
        </ReactModal>
      </SubModalContext.Provider>
    </ShellPageContext.Provider>
  );
};

/**
 * @param {import('./lib/propTypes').ModalProps} props
 */
const Modal = ({ isDirty, confirmTitle, confirmMessage, confirmKey, ...props }) => {
  if (isDirty !== undefined) {
    return (
      <DirtyTracker
        title={confirmTitle}
        // Gated on isOpen here so a closed-but-mounted modal can never arm the
        // browser-level navigation guards, regardless of what the caller passes.
        isDirty={!!isDirty && !!props.isOpen}
        message={confirmMessage}
        confirmKey={confirmKey}>
        <BaseModal isDirty={isDirty} {...props} />
      </DirtyTracker>
    );
  }
  return <BaseModal {...props} />;
};

export default Modal;
